import React, { PureComponent } from 'react';
import ValueMappers from '../ValueMappers';
import { Field, FieldProps, getIn } from 'formik';
import _isString from 'lodash/isString';
import get from 'lodash/get';
import { getFriendlyNameForUnit } from '../../../../../util/ucum';
import DateTimeField, { DateTimeFieldProps } from './DateTimeField';
import moment from 'moment';
import getSurveyItemTestId from '../../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../../util/testProps';
import {
  TextField,
  TextFieldProps,
} from '@lifeomic/chroma-react/components/TextField';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import clsx from 'clsx';
import { Text } from '@lifeomic/chroma-react/components/Text';
import MarkdownField from './MarkdownField';

export const testIds = {
  textField: (linkId: string = '') => getSurveyItemTestId(linkId),
  textFieldUnitDisplay: (linkId: string = '') =>
    getSurveyItemTestId(linkId) + '-unit-display',
};

const styles = (theme: Theme) =>
  createStyles({
    inlineTextFieldRoot: {
      display: 'flex',
      alignItems: 'center',
      // flex-start, not flex-end: with a field-width cap the input plus its
      // unit can exceed the container, and end-packing spills the overflow
      // off the left edge, chopping the first letters of the label.
      justifyContent: 'flex-start',
      minWidth: 0,
      '&&': {
        maxWidth: 'initial',
      },
    },
    inlineTextField: {
      flex: 1,
      minWidth: 0,
      // The label lives inside this flex child, so a field-width cap minus the
      // unit suffix can squeeze it into two lines. Keep it on one line; it
      // extends over the free space above the unit text rather than wrapping.
      '& label': {
        whiteSpace: 'nowrap',
      },
      '&&': {
        maxWidth: 'initial',
      },
    },
    inlineTextContainer: {
      // shrink to the unit text; a fixed 50% starved the input of half its width
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      height: '100%',
      paddingLeft: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    resizableTextArea: {
      // chroma's TextArea caps the control at maxHeight 6rem, which strands
      // the native resize handle after a slight drag. Staff type long notes
      // into these fields and need to drag them open to read or edit what is
      // already there. Doubled selector outranks chroma.
      '&&': {
        maxHeight: 'none',
        resize: 'vertical',
      },
    },
    plainTextAreaControl: {
      // `text` items are notes by definition, so start them with room for a
      // few lines. `string` items keep chroma's 2.5rem (~one line) default so
      // short answers still sit level with the fields beside them.
      '&&': {
        minHeight: '4.5rem',
      },
    },
  });

export type SupportedTypes =
  | 'string'
  | 'text'
  | 'integer'
  | 'decimal'
  | 'quantity'
  | 'time'
  | 'date'
  | 'dateTime';

const SINGLE_LINE_FHIR_TYPES: ReadonlyArray<SupportedTypes> = [
  'integer',
  'decimal',
  'quantity',
];

const isSingleLineFhirType = (fhirType: SupportedTypes) =>
  SINGLE_LINE_FHIR_TYPES.includes(fhirType);

interface TypedTextFieldOwnProps {
  fhirType: SupportedTypes;
  value: string | number | fhir.Quantity;
  initialValue?: any;
  mappingInitialValue?: any;
  enforcedPrecision?: number;
  linkId?: string;
  overrideUnitLabel?: string;
  /** Render `text` items as a plain textarea instead of the Markdown editor */
  plainTextArea?: boolean;
}

type TypedTextFieldProps = Omit<TextFieldProps, 'value'> &
  TypedTextFieldOwnProps &
  WithStyles<typeof styles>;

type TypedTextFieldState = {
  prevRawFieldValue: string | moment.Moment | null;
};

class TypedTextField extends PureComponent<
  TypedTextFieldProps,
  TypedTextFieldState
> {
  /**
   * Track previous raw field value to reconcile minor incosistencies between form state value and input string
   */
  state = {
    prevRawFieldValue: null,
  } as TypedTextFieldState;

  private getValueMapperForFieldType = () => {
    const { fhirType, enforcedPrecision } = this.props;

    switch (fhirType) {
      case 'string':
        return ValueMappers.stringTextField;
      case 'text':
        return ValueMappers.textTextField;
      case 'integer':
        return ValueMappers.integerTextField;
      case 'decimal':
        return ValueMappers.decimalTextFieldEnforcedPrecision(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          enforcedPrecision,
        );
      case 'quantity':
        return ValueMappers.quantityTextFieldEnforcedPrecision(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          enforcedPrecision,
        );
      case 'time':
        return ValueMappers.timeField;
      case 'date':
        return ValueMappers.dateField;
      case 'dateTime':
        return ValueMappers.dateTimeField;
    }
  };

  getFieldType = () => {
    const fhirType = this.props.fhirType;
    const isNumeric =
      fhirType === 'decimal' ||
      fhirType === 'quantity' ||
      fhirType === 'integer';
    return isNumeric ? 'number' : undefined;
  };

  getFieldStep = () => {
    return this.getFieldType() === 'number' ? 'any' : undefined;
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Single-line inputs inside <form> submit on Enter. That Nexts a paginated
    // page or completes a one-page survey (skipping the confirm modal). Clinic
    // vitals entry hits Enter after each value; do not treat that as submit.
    // Textarea-backed types are exempt: they never submit, and swallowing
    // Enter there would cost notes their line breaks.
    if (isSingleLineFhirType(this.props.fhirType) && e.key === 'Enter') {
      e.preventDefault();
    }
    const { onKeyDown } = this.props;
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  handleChange = (e: any | moment.Moment) => {
    if (!this.props.onChange) return;

    let newValue = null;
    if (moment.isMoment(e) || _isString(e)) {
      // time fields have straight up moment objects as values
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      newValue = e;
    } else if (e && e.target) {
      newValue = e.target.value;
    }

    const mapper = this.getValueMapperForFieldType();

    if (!mapper) {
      console.error(
        `Unable to determine a value mapper for type: "${this.props.fhirType}".  Unable to execute onChange`,
      );
      return;
    }

    this.setState({ prevRawFieldValue: newValue });

    const eventWithNewValue = {
      ...(e || {}),
      target: {
        ...get(e, 'target', {}),
        value: mapper.fromInputValue(
          // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
          newValue,
          (this.props.initialValue || this.props.mappingInitialValue) as never,
        ),
      },
    };

    this.props.onChange(eventWithNewValue);
  };

  getValue = () => {
    const mapper = this.getValueMapperForFieldType();
    if (!mapper) {
      console.error(
        `Unable to determine a value mapper for type: "${this.props.fhirType}".  Unable to map field value`,
      );
      return null;
    }

    return mapper.toInputValue(this.props.value as never);
  };

  getInLineText = (): string | null => {
    const { fhirType, initialValue, mappingInitialValue, overrideUnitLabel } =
      this.props;

    // if in error state or no units to specify
    if (fhirType !== 'quantity') return null;

    const quantity = initialValue || mappingInitialValue;
    if (!quantity) return null;

    if (overrideUnitLabel) return overrideUnitLabel;

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    const unitCode = get<fhir.Quantity, 'code', string>(quantity, 'code', null);
    if (!unitCode) return null;

    const friendlyName = getFriendlyNameForUnit(unitCode);
    if (!friendlyName) return null;

    return friendlyName;
  };

  getFieldComponent = () => {
    // all of these spread their props to the TextField root element
    switch (this.props.fhirType) {
      case 'date':
      case 'time':
      case 'dateTime':
        return DateTimeField;
      case 'text':
        // The rich-text toolbar is overkill for clinical notes; the
        // plain-text-area modifierExtension opts a question out of it.
        return this.props.plainTextArea ? TextArea : MarkdownField;
      case 'string':
        // Free text of unbounded length (MTB notes, "if other, describe"), so
        // it has to stay draggable even though it starts one line tall.
        return TextArea;
      default:
        // integer, decimal, quantity: single-line. Rendering these as
        // textareas (phc-5006) made clinic vitals look like notes.
        return TextField;
    }
  };

  getFieldComponentProps = (): any => {
    // get the non-common props per fhir type
    switch (this.props.fhirType) {
      case 'date':
        return {
          inputType: 'date',
        } as Partial<DateTimeFieldProps>;
      case 'time':
        return {
          inputType: 'time',
        } as Partial<DateTimeFieldProps>;
      case 'dateTime':
        return {
          inputType: 'dateTime',
        } as Partial<DateTimeFieldProps>;
      case 'string':
        return { textAreaClassName: this.props.classes.resizableTextArea };
      case 'text':
        // chroma's TextArea puts `className` on its wrapper div; the inner
        // <textarea> (where the height caps live) only takes textAreaClassName.
        return this.props.plainTextArea
          ? {
              textAreaClassName: clsx(
                this.props.classes.resizableTextArea,
                this.props.classes.plainTextAreaControl,
              ),
            }
          : {};
      default:
        return {};
    }
  };

  reconcileFieldValue(currentFieldValue: string | moment.Moment) {
    if (
      !this.state.prevRawFieldValue ||
      !_isString(this.state.prevRawFieldValue)
    )
      return currentFieldValue;

    /*
     * Some browsers (specifically safari based browsers) will trigger onChange with
     * "1." whereas most browsers skip this on change and will only trigger on the next
     * char (ex: "1.1").  "1." is persisted in form state as "1", but needs to repopulate the
     * field as "1."
     */
    if (
      this.getFieldType() === 'number' &&
      `${currentFieldValue}.` == this.state.prevRawFieldValue
    ) {
      return this.state.prevRawFieldValue;
    }

    return currentFieldValue;
  }

  render() {
    const {
      fhirType,
      value,
      initialValue,
      label,
      mappingInitialValue,
      hasError,
      errorMessage,
      helpMessage,
      enforcedPrecision,
      linkId,
      onChange,
      className,
      classes,
      plainTextArea,
      ...textFieldProps
    } = this.props;

    const inlineText = this.getInLineText();
    const FieldComponent = this.getFieldComponent();

    const field = (
      <FieldComponent
        label={label}
        hasError={hasError}
        helpMessage={helpMessage}
        errorMessage={errorMessage}
        type={this.getFieldType()}
        step={this.getFieldStep()}
        onChange={this.handleChange}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        value={this.reconcileFieldValue(this.getValue())}
        className={clsx(inlineText ? classes.inlineTextField : className)}
        {...this.getFieldComponentProps()}
        {...getTestProps(testIds.textField(linkId))}
        {...textFieldProps}
        onKeyDown={this.handleKeyDown}
      />
    );

    if (!inlineText) return field;

    return (
      <div className={clsx(className, classes.inlineTextFieldRoot)}>
        {field}
        <div className={classes.inlineTextContainer}>
          <Text
            size="body"
            weight="bold"
            {...getTestProps(testIds.textFieldUnitDisplay(linkId))}
          >
            {inlineText}
          </Text>
        </div>
      </div>
    );
  }
}

const TypedTextFieldWithHOC = withStyles(styles)(TypedTextField);
export default TypedTextFieldWithHOC;

interface FormikTypedTextFieldOwnProps {
  name: string;
  validate?: (value: any) => string;
}

type FormikTypedTextFieldProps = FormikTypedTextFieldOwnProps &
  Omit<TypedTextFieldOwnProps, 'value'> &
  Omit<TextFieldProps, 'value'>;

export class FormikTypedTextField extends PureComponent<FormikTypedTextFieldProps> {
  getChangeHandler = (fieldProps: FieldProps) => (e: any) => {
    const value = get(e, 'target.value');
    fieldProps.form.setFieldValue(fieldProps.field.name, value);
  };

  render() {
    const { name, validate, ...rest } = this.props;

    return (
      <Field
        name={name}
        validate={validate}
        render={(fieldProps: FieldProps) => {
          const {
            form: { isSubmitting, touched, errors },
            // field gives us things like `name`, `onChange, `onBlur`, etc.
            field,
            ...toPass
          } = fieldProps;
          const fieldError = getIn(errors, field.name);
          const showError = getIn(touched, field.name) && !!fieldError;
          return (
            <TypedTextFieldWithHOC
              className={this.props.className}
              label={this.props.label}
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              value={fieldProps.field.value}
              hasError={showError}
              errorMessage={showError && fieldError}
              disabled={this.props.disabled ?? isSubmitting}
              linkId={this.props.name}
              {...rest}
              {...toPass}
              {...field}
              onChange={this.getChangeHandler(fieldProps)}
            />
          );
        }}
      />
    );
  }
}
